import React, { useState, useEffect } from 'react'
import { format } from 'date-fns'
import { pl } from 'date-fns/locale'
import { useNotificationContext } from '../../contexts/NotificationContext'
import { useUser } from '../../hooks/useUser'
import RequirementsCreator from './RequirementsCreator'
import OrderCreator from './OrderCreator'

interface RequirementRow {
  id: string
  client_id: number
  client_name: string
  date: string
  requirement_type: 'ZM' | 'ZU' | 'ZN'
  subject: string
  status: string
  priority: string
  deadline?: string
  estimated_cost?: string
  readonly: boolean
  author_name?: string
  items_count: number
}

interface RequirementDetail extends RequirementRow {
  description?: string
  notes?: string
  items?: Array<{
    id: number
    name: string
    unit?: string
    quantity?: string
    unit_price?: string
    total_price?: string
    description?: string
    category?: string
  }>
  comments?: Array<{
    id: number
    user_id: number
    user_full_name: string
    content: string
    created_at: string
    updated_at?: string
  }>
}

interface Client { id: number; name: string }

interface RequirementsListProps { token: string }

export default function RequirementsList({ token }: RequirementsListProps) {
  const [requirements, setRequirements] = useState<RequirementRow[]>([])
  const [clients, setClients] = useState<Client[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showCreator, setShowCreator] = useState(false)
  const [editingRequirementId, setEditingRequirementId] = useState<string | undefined>(undefined)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewRequirement, setPreviewRequirement] = useState<RequirementDetail | null>(null)
  const [showOrderCreator, setShowOrderCreator] = useState(false)
  const [orderRequirementId, setOrderRequirementId] = useState<string | undefined>(undefined)
  const [orderExists, setOrderExists] = useState<{[key: string]: boolean}>({})
  const [comments, setComments] = useState<Array<{
    id: number
    user_id: number
    user_full_name: string
    content: string
    created_at: string
    updated_at?: string
  }>>([])
  const [showCommentForm, setShowCommentForm] = useState(false)
  const [newComment, setNewComment] = useState('')
  const [commentLoading, setCommentLoading] = useState(false)

  // Obsługa gestów dotykowych dla mobilnych
  const [touchStart, setTouchStart] = useState<number | null>(null)
  const [touchEnd, setTouchEnd] = useState<number | null>(null)

  // Funkcje obsługi gestów dotykowych
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null)
    setTouchStart(e.targetTouches[0].clientX)
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX)
  }

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return

    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > 50
    const isRightSwipe = distance < -50

    if (isLeftSwipe || isRightSwipe) {
      const scrollableElement = document.querySelector('.overflow-x-auto')
      if (scrollableElement) {
        const currentScroll = scrollableElement.scrollLeft
        const scrollAmount = 200

        if (isLeftSwipe) {
          scrollableElement.scrollLeft = currentScroll + scrollAmount
        } else {
          scrollableElement.scrollLeft = currentScroll - scrollAmount
        }
      }
    }
  }

  const { showSuccess, showError, showConfirm, showInfo } = useNotificationContext()

  // Filtry - wartości w polach input
  const [clientFilterInput, setClientFilterInput] = useState<number | ''>('')
  const [dateFromFilterInput, setDateFromFilterInput] = useState('')
  const [dateToFilterInput, setDateToFilterInput] = useState('')
  const [requirementTypeFilterInput, setRequirementTypeFilterInput] = useState<string>('')
  const [statusFilterInput, setStatusFilterInput] = useState<string>('')
  const [priorityFilterInput, setPriorityFilterInput] = useState<string>('')
  const [searchFilterInput, setSearchFilterInput] = useState('')

  // Filtry - aktywne wartości
  const [clientFilter, setClientFilter] = useState<number | ''>('')
  const [dateFromFilter, setDateFromFilter] = useState('')
  const [dateToFilter, setDateToFilter] = useState('')
  const [requirementTypeFilter, setRequirementTypeFilter] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [priorityFilter, setPriorityFilter] = useState<string>('')
  const [searchFilter, setSearchFilter] = useState('')

  // Sortowanie
  const [sortBy, setSortBy] = useState('date')
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC')

  useEffect(() => {
    loadClients()
    loadRequirements()
  }, [])

  useEffect(() => {
    loadRequirements()
  }, [clientFilter, dateFromFilter, dateToFilter, requirementTypeFilter, statusFilter, priorityFilter, searchFilter, sortBy, sortOrder])

  const handleFilterClick = () => {
    setClientFilter(clientFilterInput)
    setDateFromFilter(dateFromFilterInput)
    setDateToFilter(dateToFilterInput)
    setRequirementTypeFilter(requirementTypeFilterInput)
    setStatusFilter(statusFilterInput)
    setPriorityFilter(priorityFilterInput)
    setSearchFilter(searchFilterInput)
  }

  const clearInputFilters = () => {
    setClientFilterInput('')
    setDateFromFilterInput('')
    setDateToFilterInput('')
    setRequirementTypeFilterInput('')
    setStatusFilterInput('')
    setPriorityFilterInput('')
    setSearchFilterInput('')
  }

  const clearActiveFilters = () => {
    setClientFilter('')
    setDateFromFilter('')
    setDateToFilter('')
    setRequirementTypeFilter('')
    setStatusFilter('')
    setPriorityFilter('')
    setSearchFilter('')
  }

  const clearFilters = () => {
    clearInputFilters()
    clearActiveFilters()
  }

  const loadClients = async () => {
    try {
      const response = await fetch('/api/clients/list', {
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (response.ok) {
        const data = await response.json()
        setClients(data)
      }
    } catch (err) {
      console.error('Error loading clients:', err)
    }
  }

  const loadRequirements = async () => {
    try {
      setLoading(true)
      setError('')
      const params = new URLSearchParams()
      if (clientFilter) params.append('client_id', clientFilter.toString())
      if (dateFromFilter) params.append('date_from', dateFromFilter)
      if (dateToFilter) params.append('date_to', dateToFilter)
      if (requirementTypeFilter) params.append('requirement_type', requirementTypeFilter)
      if (statusFilter) params.append('status', statusFilter)
      if (priorityFilter) params.append('priority', priorityFilter)
      if (searchFilter) params.append('subject_search', searchFilter)
      params.append('sort_by', sortBy)
      params.append('sort_order', sortOrder)

      const response = await fetch(`/api/requirements?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      })

      if (!response.ok) {
        setError('Błąd podczas ładowania zapotrzebowań')
        setRequirements([])
        return
      }

      const list: RequirementRow[] = await response.json()
      setRequirements(list)

      // Sprawdź zamówienia dla zatwierdzonych zapotrzebowań
      const approvedRequirements = list.filter(req => req.status === 'zatwierdzone')
      for (const req of approvedRequirements) {
        await checkOrderExists(req.id)
      }
    } catch (err) {
      setError('Błąd połączenia z serwerem')
      setRequirements([])
    } finally {
      setLoading(false)
    }
  }

  const openRequirementsCreator = () => setShowCreator(true)

  const deleteRequirement = async (requirementId: string) => {
    const confirmed = await showConfirm('Potwierdzenie usunięcia', `Czy na pewno chcesz usunąć zapotrzebowanie ${requirementId}?`)
    if (!confirmed) return
    try {
      const response = await fetch(`/api/requirements/${requirementId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (response.ok) {
        showSuccess('Zapotrzebowanie zostało usunięte pomyślnie')
        loadRequirements()
      } else {
        const errorData = await response.json()
        showError('Błąd usuwania', errorData.detail || 'Błąd podczas usuwania zapotrzebowania')
      }
    } catch {
      showError('Błąd połączenia', 'Nie można połączyć się z serwerem')
    }
  }

  const markReadonly = async (requirementId: string) => {
    const confirmed = await showConfirm('Potwierdzenie zatwierdzenia', `Czy na pewno chcesz oznaczyć zapotrzebowanie ${requirementId} jako zatwierdzone?`)
    if (!confirmed) return
    try {
      const response = await fetch(`/api/requirements/${requirementId}/readonly`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (response.ok) {
        showSuccess('Zapotrzebowanie zostało zatwierdzone pomyślnie')
        loadRequirements()
      } else {
        const errorData = await response.json()
        showError('Błąd zatwierdzania', errorData.detail || 'Błąd podczas zatwierdzania zapotrzebowania')
      }
    } catch {
      showError('Błąd połączenia', 'Nie można połączyć się z serwerem')
    }
  }

  const approveRequirement = async (requirementId: string) => {
    const confirmed = await showConfirm('Potwierdzenie zatwierdzenia', `Czy na pewno chcesz zatwierdzić zapotrzebowanie ${requirementId}?`)
    if (!confirmed) return
    try {
      const response = await fetch(`/api/requirements/${requirementId}/approve`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (response.ok) {
        showSuccess('Zapotrzebowanie zostało zatwierdzone pomyślnie')
        loadRequirements()
      } else {
        const errorData = await response.json()
        showError('Błąd zatwierdzania', errorData.detail || 'Błąd podczas zatwierdzania zapotrzebowania')
      }
    } catch {
      showError('Błąd połączenia', 'Nie można połączyć się z serwerem')
    }
  }

  const rejectRequirement = async (requirementId: string) => {
    const confirmed = await showConfirm('Potwierdzenie odrzucenia', `Czy na pewno chcesz odrzucić zapotrzebowanie ${requirementId}?`)
    if (!confirmed) return
    try {
      const response = await fetch(`/api/requirements/${requirementId}/reject`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (response.ok) {
        showSuccess('Zapotrzebowanie zostało odrzucone pomyślnie')
        loadRequirements()
      } else {
        const errorData = await response.json()
        showError('Błąd odrzucania', errorData.detail || 'Błąd podczas odrzucania zapotrzebowania')
      }
    } catch {
      showError('Błąd połączenia', 'Nie można połączyć się z serwerem')
    }
  }

  const orderRequirement = async (requirementId: string) => {
    const confirmed = await showConfirm('Potwierdzenie zamówienia', `Czy na pewno chcesz zamówić zapotrzebowanie ${requirementId}?`)
    if (!confirmed) return
    try {
      const response = await fetch(`/api/requirements/${requirementId}/order`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (response.ok) {
        showSuccess('Zapotrzebowanie zostało zamówione pomyślnie')
        loadRequirements()
      } else {
        const errorData = await response.json()
        showError('Błąd zamawiania', errorData.detail || 'Błąd podczas zamawiania zapotrzebowania')
      }
    } catch {
      showError('Błąd połączenia', 'Nie można połączyć się z serwerem')
    }
  }

  const deliverRequirement = async (requirementId: string) => {
    const confirmed = await showConfirm('Potwierdzenie dostarczenia', `Czy na pewno chcesz oznaczyć zapotrzebowanie ${requirementId} jako dostarczone?`)
    if (!confirmed) return
    try {
      const response = await fetch(`/api/requirements/${requirementId}/deliver`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (response.ok) {
        showSuccess('Zapotrzebowanie zostało oznaczone jako dostarczone pomyślnie')
        loadRequirements()
      } else {
        const errorData = await response.json()
        showError('Błąd dostarczania', errorData.detail || 'Błąd podczas oznaczania jako dostarczone')
      }
    } catch {
      showError('Błąd połączenia', 'Nie można połączyć się z serwerem')
    }
  }

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC')
    } else {
      setSortBy(field)
      setSortOrder('DESC')
    }
  }

  const getSortIcon = (field: string) => {
    if (sortBy !== field) return '↕️'
    return sortOrder === 'ASC' ? '↑' : '↓'
  }

  // Podgląd zapotrzebowania
  const openPreview = async (requirementId: string) => {
    setPreviewOpen(true)
    setPreviewLoading(true)
    setPreviewRequirement(null)
    setComments([])
    setShowCommentForm(false)
    setNewComment('')
    try {
      const resp = await fetch(`/api/requirements/${requirementId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (!resp.ok) throw new Error()
      const data: RequirementDetail = await resp.json()
      setPreviewRequirement(data)
      // Załaduj komentarze
      await loadComments(requirementId)
    } catch {
      showError('Błąd', 'Nie udało się pobrać danych zapotrzebowania do podglądu.')
      setPreviewOpen(false)
    } finally {
      setPreviewLoading(false)
    }
  }

  const closePreview = () => {
    setPreviewOpen(false)
    setPreviewRequirement(null)
    setPreviewLoading(false)
    setComments([])
    setShowCommentForm(false)
    setNewComment('')
  }

  // Ładowanie komentarzy
  const loadComments = async (requirementId: string) => {
    try {
      const resp = await fetch(`/api/comments?resource_type=requirement&resource_id=${requirementId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (resp.ok) {
        const commentsData = await resp.json()
        setComments(commentsData)
      }
    } catch (err) {
      console.error('Error loading comments:', err)
    }
  }

  // Dodawanie komentarza
  const addComment = async () => {
    if (!newComment.trim() || !previewRequirement) return

    setCommentLoading(true)
    try {
      const resp = await fetch('/api/comments', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          resource_type: 'requirement',
          resource_id: previewRequirement.id,
          content: newComment.trim()
        })
      })

      if (resp.ok) {
        setNewComment('')
        setShowCommentForm(false)
        showSuccess('Komentarz został dodany pomyślnie')
        await loadComments(previewRequirement.id)
      } else {
        const errorData = await resp.json()
        showError('Błąd', errorData.detail || 'Nie udało się dodać komentarza')
      }
    } catch (err) {
      showError('Błąd', 'Nie udało się dodać komentarza')
    } finally {
      setCommentLoading(false)
    }
  }

  // Otwórz kreator zleceń
  const openOrderCreator = (requirementId: string) => {
    setOrderRequirementId(requirementId)
    setShowOrderCreator(true)
  }

  const closeOrderCreator = () => {
    setShowOrderCreator(false)
    setOrderRequirementId(undefined)
  }



  // Funkcja sprawdzająca czy zamówienie istnieje dla zapotrzebowania
  const checkOrderExists = async (requirementId: string) => {
    try {
      const response = await fetch(`/api/orders?requirement_id=${requirementId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (response.ok) {
        const orders = await response.json()
        setOrderExists(prev => ({
          ...prev,
          [requirementId]: orders.length > 0
        }))
      }
    } catch (err) {
      console.error('Error checking order existence:', err)
    }
  }

  // Funkcja resetowania zamówienia
  const resetOrder = async (requirementId: string) => {
    const confirmed = await showConfirm(
      'Potwierdzenie resetowania zamówienia',
      `Czy na pewno chcesz zresetować zamówienie dla zapotrzebowania ${requirementId}? Ta operacja usunie istniejące zamówienie i pozwoli na utworzenie nowego.`
    )
    if (!confirmed) return

    try {
      const response = await fetch(`/api/orders/reset/${requirementId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (response.ok) {
        const result = await response.json()
        showSuccess('Zamówienie zostało zresetowane pomyślnie', `ID zamówienia: ${result.order_id}`)
        // Odśwież informacje o zamówieniu
        await checkOrderExists(requirementId)
      } else {
        const errorData = await response.json()
        showError('Błąd resetowania', errorData.detail || 'Błąd podczas resetowania zamówienia')
      }
    } catch {
      showError('Błąd połączenia', 'Nie można połączyć się z serwerem')
    }
  }

  // Funkcja pobierania PDF zamówienia
  const downloadOrderPDF = async (requirementId: string) => {
    try {
      // Najpierw pobierz informacje o zamówieniu
      const ordersResponse = await fetch(`/api/orders?requirement_id=${requirementId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      })

      if (!ordersResponse.ok) {
        showError('Błąd', 'Nie udało się pobrać informacji o zamówieniu')
        return
      }

      const orders = await ordersResponse.json()
      if (orders.length === 0) {
        showError('Błąd', 'Nie znaleziono zamówienia dla tego zapotrzebowania')
        return
      }

      const order = orders[0]

      // Pobierz PDF zamówienia
      const pdfResponse = await fetch(`/api/orders/pdf/${order.id}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      })

      if (!pdfResponse.ok) {
        showError('Błąd', 'Nie udało się pobrać pliku PDF zamówienia')
        return
      }

      // Pobierz plik jako blob
      const blob = await pdfResponse.blob()

      // Utwórz URL do pobrania
      const url = window.URL.createObjectURL(blob)

      // Utwórz link do pobrania i kliknij go automatycznie
      const link = document.createElement('a')
      link.href = url
      link.download = `zlecenie_${order.id}.pdf`
      document.body.appendChild(link)
      link.click()

      // Wyczyść URL
      window.URL.revokeObjectURL(url)
      document.body.removeChild(link)

      showSuccess('PDF zamówienia został pobrany pomyślnie')
    } catch (err) {
      console.error('Error downloading PDF:', err)
      showError('Błąd', 'Nie udało się pobrać pliku PDF zamówienia')
    }
  }

  const getRequirementTypeLabel = (type: string) => {
    const labels = { 'ZM': 'Zapotrzebowanie materiałowe', 'ZU': 'Zapotrzebowanie usługowe', 'ZN': 'Zapotrzebowanie naprawcze' }
    return labels[type as keyof typeof labels] || type
  }

  const getStatusLabel = (status: string) => {
    const labels = {
      'utworzone': 'Utworzone',
      'zatwierdzone': 'Zatwierdzone',
      'odrzucony': 'Odrzucony',
      'zamówione': 'Zamówione',
      'dostarczone': 'Dostarczone'
    }
    return labels[status as keyof typeof labels] || status
  }

  const getPriorityLabel = (priority: string) => {
    const labels = { 'low': 'Niski', 'medium': 'Średni', 'high': 'Wysoki' }
    return labels[priority as keyof typeof labels] || priority
  }

  if (loading && requirements.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2">Ładowanie zapotrzebowań...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Zapotrzebowania</h2>
        <button
          onClick={openRequirementsCreator}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium"
        >
          + Nowe zapotrzebowanie
        </button>
      </div>

      {/* Filtry */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Filtry</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Klient */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Klient</label>
            <select
              value={clientFilterInput}
              onChange={(e) => setClientFilterInput(e.target.value === '' ? '' : parseInt(e.target.value))}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Wszyscy klienci</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>{client.name}</option>
              ))}
            </select>
          </div>

          {/* Data od */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data od</label>
            <input
              type="date"
              value={dateFromFilterInput}
              onChange={(e) => setDateFromFilterInput(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Data do */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data do</label>
            <input
              type="date"
              value={dateToFilterInput}
              onChange={(e) => setDateToFilterInput(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Typ zapotrzebowania */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Typ</label>
            <select
              value={requirementTypeFilterInput}
              onChange={(e) => setRequirementTypeFilterInput(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Wszystkie</option>
              <option value="ZM">Zapotrzebowanie materiałowe</option>
              <option value="ZU">Zapotrzebowanie usługowe</option>
              <option value="ZN">Zapotrzebowanie naprawcze</option>
            </select>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={statusFilterInput}
              onChange={(e) => setStatusFilterInput(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Wszystkie</option>
              <option value="utworzone">Utworzone</option>
              <option value="zatwierdzone">Zatwierdzone</option>
              <option value="odrzucony">Odrzucony</option>
              <option value="zamówione">Zamówione</option>
              <option value="dostarczone">Dostarczone</option>
            </select>
          </div>

          {/* Priorytet */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Priorytet</label>
            <select
              value={priorityFilterInput}
              onChange={(e) => setPriorityFilterInput(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Wszystkie</option>
              <option value="low">Niski</option>
              <option value="medium">Średni</option>
              <option value="high">Wysoki</option>
            </select>
          </div>

          {/* Szukaj tematu */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Szukaj tematu</label>
            <input
              type="text"
              value={searchFilterInput}
              onChange={(e) => setSearchFilterInput(e.target.value)}
              placeholder="Temat zapotrzebowania..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Przyciski */}
          <div className="flex items-end gap-2">
            <button
              onClick={handleFilterClick}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium"
            >
              Filtruj
            </button>
            <button
              onClick={clearFilters}
              className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md font-medium"
            >
              Wyczyść filtry
            </button>
          </div>
        </div>
      </div>

      {/* Błąd */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      {/* Tabela zapotrzebowań */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div
          className="overflow-x-auto"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {/* Data utworzenia */}
                <th
                  className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 w-20 sm:w-auto"
                  onClick={() => handleSort('date')}
                >
                  <span className="hidden sm:inline">Data utworzenia </span>
                  <span className="sm:hidden">Data </span>
                  {getSortIcon('date')}
                </th>

                {/* Numer (ID) */}
                <th
                  className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 w-16 sm:w-auto"
                  onClick={() => handleSort('id')}
                >
                  Numer {getSortIcon('id')}
                </th>

                {/* Typ */}
                <th
                  className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('requirement_type')}
                >
                  Typ {getSortIcon('requirement_type')}
                </th>

                {/* Temat */}
                <th className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Temat
                </th>

                {/* Status */}
                <th
                  className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('status')}
                >
                  Status {getSortIcon('status')}
                </th>

                {/* Akcje */}
                <th className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20 sm:w-auto">
                  Akcje
                </th>
              </tr>
            </thead>

            <tbody className="bg-white divide-y divide-gray-200">
              {requirements.map((requirement) => (
                <tr key={requirement.id} className="hover:bg-gray-50">
                  {/* Data */}
                  <td className="px-2 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className="sm:hidden font-medium">
                      {format(new Date(requirement.date), 'dd.MM', { locale: pl })}
                    </span>
                    <span className="hidden sm:inline">
                      {format(new Date(requirement.date), 'dd.MM.yyyy', { locale: pl })}
                    </span>
                  </td>

                  {/* Numer */}
                  <td className={`px-2 sm:px-6 py-4 whitespace-nowrap text-sm font-medium ${
                    requirement.readonly ? 'text-green-600' : 'text-blue-500'
                  }`}>
                    <button
                      onClick={() => openPreview(requirement.id)}
                      className="inline-block px-2 py-1 rounded border-2 hover:bg-gray-100 transition-colors"
                      title="Kliknij aby zobaczyć podgląd zapotrzebowania"
                    >
                      {requirement.id}
                    </button>
                  </td>

                  {/* Typ */}
                  <td className="px-2 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {getRequirementTypeLabel(requirement.requirement_type)}
                  </td>

                  {/* Temat */}
                  <td className="px-2 sm:px-6 py-4 text-sm text-gray-900">
                    <div className="max-w-xs truncate">
                      {requirement.subject}
                    </div>
                  </td>

                  {/* Status */}
                  <td className="px-2 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {getStatusLabel(requirement.status)}
                  </td>

                  {/* Akcje */}
                  <td className="px-2 sm:px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex flex-wrap gap-1">
                      <button
                        onClick={() => {
                          setEditingRequirementId(requirement.id)
                          setShowCreator(true)
                        }}
                        disabled={requirement.readonly}
                        className={`px-2 py-1 rounded text-xs ${
                          requirement.readonly
                            ? 'text-gray-400 cursor-not-allowed bg-gray-100'
                            : 'text-blue-600 hover:text-blue-900 hover:bg-blue-50'
                        }`}
                      >
                        Edytuj
                      </button>

                      {/* Actions for "utworzone" status - available for authorized roles */}
                      {requirement.status === 'utworzone' && !requirement.readonly && (
                        <>
                          <button
                            onClick={() => approveRequirement(requirement.id)}
                            className="px-2 py-1 rounded text-xs text-green-600 hover:text-green-900 hover:bg-green-50"
                          >
                            Zatwierdź
                          </button>
                          <button
                            onClick={() => rejectRequirement(requirement.id)}
                            className="px-2 py-1 rounded text-xs text-orange-600 hover:text-orange-900 hover:bg-orange-50"
                          >
                            Odrzuć
                          </button>
                        </>
                      )}

                      {/* Actions for "zatwierdzone" status - available for authorized roles */}
                      {requirement.status === 'zatwierdzone' && (
                        <>
                          <button
                            onClick={() => orderRequirement(requirement.id)}
                            className="px-2 py-1 rounded text-xs text-purple-600 hover:text-purple-900 hover:bg-purple-50"
                          >
                            Zamówione
                          </button>
                          <button
                            onClick={() => openOrderCreator(requirement.id)}
                            className="px-2 py-1 rounded text-xs text-green-600 hover:text-green-900 hover:bg-green-50"
                          >
                            Stwórz zlecenie
                          </button>
                          {/* Download PDF button - for authorized users when order exists */}
                          {orderExists[requirement.id] && (
                            <button
                              onClick={() => downloadOrderPDF(requirement.id)}
                              className="px-2 py-1 rounded text-xs text-blue-600 hover:text-blue-900 hover:bg-blue-50"
                              title="Pobierz PDF zamówienia"
                            >
                              📄 PDF
                            </button>
                          )}
                          {/* Reset order button - only for MASTER_ADMIN */}
                          {orderExists[requirement.id] && (
                            <button
                              onClick={() => resetOrder(requirement.id)}
                              className="px-2 py-1 rounded text-xs text-red-600 hover:text-red-900 hover:bg-red-50"
                              title="Resetuj zamówienie (tylko MASTER_ADMIN)"
                            >
                              Reset zamówienia
                            </button>
                          )}
                        </>
                      )}

                      {/* All users can deliver "zamówione" items */}
                      {requirement.status === 'zamówione' && (
                        <button
                          onClick={() => deliverRequirement(requirement.id)}
                          className="px-2 py-1 rounded text-xs text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50"
                        >
                          Dostarczone
                        </button>
                      )}

                      {/* Delete action - admin roles can delete any requirement, regular users only non-readonly */}
                      {(!requirement.readonly) && (
                        <button
                          onClick={() => deleteRequirement(requirement.id)}
                          className="px-2 py-1 rounded text-xs text-red-600 hover:text-red-900 hover:bg-red-50"
                        >
                          Usuń
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {requirements.length === 0 && !loading && (
          <div className="text-center py-8 text-gray-500">
            Brak zapotrzebowań spełniających kryteria wyszukiwania
          </div>
        )}
      </div>

      {/* Podsumowanie */}
      <div className="bg-blue-50 p-4 rounded-lg">
        <div className="flex justify-between items-center text-sm text-blue-800">
          <span>Znaleziono zapotrzebowań: <strong>{requirements.length}</strong></span>
        </div>
      </div>

      {/* Modal kreatora/edycji */}
      <RequirementsCreator
        token={token}
        isOpen={showCreator}
        onClose={() => {
          setShowCreator(false)
          setEditingRequirementId(undefined)
        }}
        onRequirementCreated={loadRequirements}
        requirementId={editingRequirementId}
      />

      {/* Modal kreatora zleceń */}
      <OrderCreator
        token={token}
        isOpen={showOrderCreator}
        onClose={closeOrderCreator}
        requirementId={orderRequirementId}
        onOrderCreated={loadRequirements}
      />

      {/* Modal PODGLĄDU */}
      {previewOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl mx-4 overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-xl font-semibold text-gray-900">Podgląd zapotrzebowania</h3>
              <button onClick={closePreview} className="text-gray-400 hover:text-gray-600" aria-label="Zamknij">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <div className="p-6 max-h-[80vh] overflow-y-auto">
              {previewLoading && (
                <div className="flex items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-2">Ładowanie…</span>
                </div>
              )}

              {!previewLoading && previewRequirement && (
                <div className="bg-white mx-auto shadow print:shadow-none">
                  <div className="mx-auto bg-white border border-gray-200 rounded-md p-8 w-[900px]">
                    {/* Nagłówek */}
                    <div className="flex items-start justify-between mb-6">
                      <div>
                        <h1 className="text-2xl font-bold text-gray-900">{getRequirementTypeLabel(previewRequirement.requirement_type)}</h1>
                        <p className="text-sm text-gray-600">Nr: <span className="font-medium text-gray-900">{previewRequirement.id}</span></p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">Data zapotrzebowania</p>
                        <p className="text-base font-medium text-gray-900">
                          {format(new Date(previewRequirement.date), 'dd.MM.yyyy', { locale: pl })}
                        </p>
                      </div>
                    </div>

                    {/* Dane podstawowe */}
                    <div className="grid grid-cols-2 gap-6 mb-6">
                      <div className="space-y-1">
                        <div className="text-sm text-gray-500">Klient</div>
                        <div className="text-base font-medium text-gray-900">{previewRequirement.client_name}</div>
                      </div>
                      <div className="space-y-1">
                        <div className="text-sm text-gray-500">Autor</div>
                        <div className="text-base font-medium text-gray-900">{previewRequirement.author_name || '(brak danych)'}</div>
                      </div>
                      <div className="space-y-1">
                        <div className="text-sm text-gray-500">Status</div>
                        <div className="text-base font-medium text-gray-900">{getStatusLabel(previewRequirement.status)}</div>
                      </div>
                      <div className="space-y-1">
                        <div className="text-sm text-gray-500">Priorytet</div>
                        <div className="text-base font-medium text-gray-900">{getPriorityLabel(previewRequirement.priority)}</div>
                      </div>
                    </div>

                    {/* Temat */}
                    <div className="mb-6">
                      <h2 className="text-lg font-semibold text-gray-900 mb-2">Temat</h2>
                      <div className="text-gray-900">{previewRequirement.subject}</div>
                    </div>

                    {/* Opis */}
                    {previewRequirement.description && (
                      <div className="mb-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-2">Opis</h2>
                        <div className="whitespace-pre-wrap text-gray-900">{previewRequirement.description}</div>
                      </div>
                    )}

                    {/* Pozycje */}
                    {!!previewRequirement.items?.length && (
                      <div className="mb-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-3">Pozycje</h2>
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nazwa</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ilość</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Jedn.</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cena jedn.</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Razem</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {previewRequirement.items.map((item, index) => (
                                <tr key={item.id || index}>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.name}</td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.quantity || '-'}</td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.unit || '-'}</td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.unit_price || '-'}</td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.total_price || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Dodatkowe informacje */}
                    {previewRequirement.notes && (
                      <div className="mb-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-2">Dodatkowe informacje</h2>
                        <div className="whitespace-pre-wrap text-gray-900">{previewRequirement.notes}</div>
                      </div>
                    )}

                    {/* Komentarze */}
                    <div className="mb-6">
                      <div className="flex items-center justify-between mb-3">
                        <h2 className="text-lg font-semibold text-gray-900">Komentarze</h2>
                        {!previewRequirement.readonly && (
                          <button
                            onClick={() => setShowCommentForm(!showCommentForm)}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md text-sm font-medium"
                          >
                            {showCommentForm ? 'Anuluj' : '+ Dodaj komentarz'}
                          </button>
                        )}
                      </div>

                      {/* Formularz dodawania komentarza */}
                      {showCommentForm && !previewRequirement.readonly && (
                        <div className="mb-4 p-4 border border-gray-200 rounded-md bg-gray-50">
                          <textarea
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            placeholder="Wpisz swój komentarz..."
                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                            rows={3}
                            maxLength={2000}
                          />
                          <div className="flex justify-between items-center mt-2">
                            <span className="text-xs text-gray-500">
                              {newComment.length}/2000 znaków
                            </span>
                            <div className="flex gap-2">
                              <button
                                onClick={() => {
                                  setShowCommentForm(false)
                                  setNewComment('')
                                }}
                                className="px-3 py-1 text-sm bg-gray-300 hover:bg-gray-400 rounded-md"
                              >
                                Anuluj
                              </button>
                              <button
                                onClick={addComment}
                                disabled={!newComment.trim() || commentLoading}
                                className="px-3 py-1 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {commentLoading ? 'Dodawanie...' : 'Dodaj'}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Lista komentarzy */}
                      {comments.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          Brak komentarzy
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {comments.map((comment) => (
                            <div key={comment.id} className="border border-gray-200 rounded-md p-4">
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-gray-900">{comment.user_full_name}</span>
                                  <span className="text-xs text-gray-500">
                                    {format(new Date(comment.created_at), 'dd.MM.yyyy HH:mm', { locale: pl })}
                                  </span>
                                </div>
                              </div>
                              <div className="text-gray-900 whitespace-pre-wrap">{comment.content}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 px-6 py-4 border-t">
              <button onClick={closePreview} className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-md">
                Zamknij
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
